import { API_BASE_URL } from '../config';

export interface PaymentCreateResponse {
  success: boolean;
  order?: {
    id: string;
    amount: number;
    currency: string;
  };
  message?: string;
}

export interface PaymentVerifyResponse {
  success: boolean;
  message: string;
}

const getFullUrl = (path: string) => {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return `${API_BASE_URL}${path}`;
  }
  return path;
};

export interface DuplicateCheckResponse {
  success: boolean;
  hasDuplicate: boolean;
  duplicateItems?: Array<{ productId: string; title: string }>;
  existingOrderId?: string;
  existingOrderDisplayId?: string;
  existingOrderDate?: string;
  existingOrderStatus?: string;
  message?: string;
}

export async function checkDuplicateOrder(
  items: { productId: string; quantity?: number }[],
  token: string
): Promise<DuplicateCheckResponse> {
  const res = await fetch(getFullUrl('/api/payment/check-duplicate-order'), {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ items }),
  });
  
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    return { success: false, hasDuplicate: false, message: errorData.message || 'Failed to check duplicate order' };
  }
  return res.json();
}

export async function createRazorpayOrder(
  items: { productId: string; quantity: number; selectedColor?: string }[],
  shippingAddress: any,
  token: string,
  confirmDuplicate?: boolean
): Promise<PaymentCreateResponse & Partial<DuplicateCheckResponse>> {
  const res = await fetch(getFullUrl('/api/payment/create-order'), {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ items, shippingAddress, confirmDuplicate }),
  });
  
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 409 && data.hasDuplicate) {
      return { success: false, ...data };
    }
    throw new Error(data.message || 'Failed to create order');
  }
  return data;
}

export async function createCodOrder(
  items: { productId: string; quantity: number; selectedColor?: string }[],
  shippingAddress: any,
  token: string,
  confirmDuplicate?: boolean
): Promise<PaymentCreateResponse & Partial<DuplicateCheckResponse>> {
  const res = await fetch(getFullUrl('/api/payment/create-cod-order'), {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ items, shippingAddress, confirmDuplicate }),
  });
  
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 409 && data.hasDuplicate) {
      return { success: false, ...data };
    }
    throw new Error(data.message || 'Failed to create COD order');
  }
  return data;
}

export async function verifyRazorpayPayment(
  orderId: string,
  paymentId: string,
  signature: string
): Promise<PaymentVerifyResponse> {
  const res = await fetch(getFullUrl('/api/payment/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    }),
  });
  
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to verify payment');
  }
  return res.json();
}

export async function getRazorpayConfig(): Promise<{ keyId: string }> {
  const res = await fetch(getFullUrl('/api/payment/config'));
  if (!res.ok) {
    throw new Error('Failed to load payment configuration');
  }
  return res.json();
}
